import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import img1 from "../../assets/hero/image.png";
import img2 from "../../assets/whoWeAre/partners/img1.png";
import img3 from "../../assets/whoWeAre/partners/img2.png";
import img4 from "../../assets/whoWeAre/partners/img4.png";
import gran from "../home/whoweare/gran.png";

const teamMembers = [
  {
    name: "Muthu Lakshmi J ",
    role: "Partner",
    image: img2,
  },
  {
    name: "Vanitha V",
    role: "Partner",
    image: img4,
  },
  {
    name: "Kavitha V",
    role: "Partner",
    image: img3,
  },
  {
    name: "V.V Mukesh",
    role: "Administrator",
    image: gran,
  },
  {
    name: "R. S. Jeyapaul",
    role: "Managing Director",
    image: img1,
  },
];

// Counter hook
function useCounter(target, duration, trigger) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!trigger) return;
    let startValue = 0;
    const totalFrames = Math.round((duration / 1000) * 60);
    let frame = 0;

    const counter = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      setCount(Math.floor(progress * (target - startValue) + startValue));
      if (frame === totalFrames) clearInterval(counter);
    }, 1000 / 60);

    return () => clearInterval(counter);
  }, [target, duration, trigger]);

  return count;
}

// Animation Variants
const container = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const letter = {
  hidden: { opacity: 0, y: "0.25em" },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

export default function MeetTheTeam() {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect(); // ✅ trigger only once
        }
      },
      {
        threshold: 0.1, // ✅ low threshold for mobile
        rootMargin: "0px 0px -50px 0px", // ✅ ensures mobile triggers earlier
      }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const animatedNumber = useCounter(250, 1000, isInView);
  const headingText = "Meet the team";

  const specialMember = teamMembers[4];
  const otherMembers = teamMembers.filter((m, i) => i !== 4);

  return (
    <div className="font-sans text-gray-800 bg-blue-100">
      <section
        ref={sectionRef}
        className="px-4 sm:px-6 md:px-12 lg:px-20 py-16 bg-white"
      >
        {/* Heading */}
        <div className="mb-12">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            <span className="text-base sm:text-lg font-medium">
              Our Awesome Team
            </span>
          </span>

          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl text-black font-semibold mt-2 flex flex-wrap"
            variants={container}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {headingText.split("").map((char, i) => (
              <motion.span key={i} variants={letter}>
                {char === " " ? "\u00A0" : char}
              </motion.span>
            ))}
          </motion.h2>
        </div>

        {/* Top Row: Special Member + Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 items-stretch">
          {/* Left: Special Member */}
          <div className="text-center group flex flex-col">
            <div className="relative overflow-hidden rounded-2xl flex-1">
              <img
                src={specialMember.image}
                alt={specialMember.name}
                className="w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-110 rounded-2xl"
              />
            </div>
            <div className="flex flex-col justify-center items-start mt-4">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold">
                {specialMember.name}
              </h3>
              <p className="text-gray-500 text-sm sm:text-base md:text-lg">
                {specialMember.role}
              </p>
            </div>
          </div>

          {/* Right: Stats */}
          <div className="h-full bg-gray-50 rounded-2xl p-6 sm:p-10 lg:p-[54px] flex flex-col justify-center text-center md:text-left">
            <h3 className="text-2xl sm:text-3xl md:text-[28px] font-semibold text-black mb-2">
              Our Awesome Team
            </h3>
            <p className="text-gray-600 mb-6 text-base sm:text-lg">
              Dedicated to innovation, quality, and customer trust
for the organization’s strong reputation over four decades.
            </p>
            <div className="text-4xl sm:text-5xl md:text-[64px] text-black font-semibold mb-2 mt-6">
              {animatedNumber}+
            </div>
            <p className="text-black text-lg sm:text-xl md:text-2xl font-medium mb-6">
              Awesome team members
            </p>
            <Link
              to="/team"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <button className="relative overflow-hidden w-fit inline-flex items-center gap-2 px-6 py-3 sm:py-4 rounded-full border border-white hover:border-black text-white hover:text-black bg-[#000d2f] hover:bg-white font-medium group transition-colors duration-300">
                <span className="relative z-10 flex items-center gap-2">
                  <span className="relative block overflow-hidden h-6">
                    <span className="block transition-transform duration-300 transform group-hover:-translate-y-full">
                      Meet our team
                    </span>
                    <span className="absolute top-full left-0 w-full block text-black transition-transform duration-300 transform group-hover:-translate-y-full">
                      Meet our team
                    </span>
                  </span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </span>
              </button>
            </Link>
          </div>
        </div>

        {/* Other Team Members */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          {otherMembers.map((member, index) => (
            <div key={index} className="text-center group">
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-80 transition-transform duration-500 ease-in-out group-hover:scale-110 rounded-2xl object-cover"
                />
              </div>
              <div className="flex flex-col justify-center items-start mt-4">
                <h3 className="text-lg sm:text-xl md:text-2xl font-semibold">
                  {member.name}
                </h3>
                <p className="text-gray-500 text-sm sm:text-base md:text-lg">
                  {member.role}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
